import { MapPin, Pencil, ChevronDown, PencilLine } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useMapStore } from '@/stores/mapStore';
import { useMap } from '@/providers/MapProvider';
import { cn } from '@/lib/utils';
import VectorLayer from 'ol/layer/Vector';
import { createEmpty, extend } from 'ol/extent';
import { FieldCorrection } from '@/features/ticket/components/TicketDetailsDialog/CorrectionTimeline';

const STATUS_CONFIG = {
  PENDING: {
    label: 'Pending',
    color: '#f59e0b',
    strokeColor: '#f59e0b',
  },
  APPROVED: {
    label: 'Approved',
    color: '#22c55e',
    strokeColor: '#22c55e',
  },
  REJECTED: {
    label: 'Rejected',
    color: '#ef4444',
    strokeColor: '#ef4444',
  },
} as const;

type CorrectionStatus = keyof typeof STATUS_CONFIG;

export const CorrectionOverlayPanel = () => {
  const { map } = useMap();

  const overlayCorrections = useMapStore((s) => s.overlayCorrections);
  const overlayVisible = useMapStore((s) => s.overlayVisible);
  const setOverlayVisible = useMapStore((s) => s.setOverlayVisible);
  const highlightedCorrectionId = useMapStore((s) => s.highlightedCorrectionId);
  const setHighlightedCorrection = useMapStore(
    (s) => s.setHighlightedCorrection,
  );
  const requestEditCorrection = useMapStore((s) => s.requestEditCorrection);

  if (overlayCorrections.length === 0) return null;

  const zoomToCorrection = (correctionId: string) => {
    if (!map) return;

    const overlayLayer = map
      .getLayers()
      .getArray()
      .find((l) => l.get('type') === 'correction-overlay') as
      | VectorLayer
      | undefined;

    if (!overlayLayer) return;
    const source = overlayLayer.getSource();
    if (!source) return;

    const extent = createEmpty();
    let found = false;

    source.getFeatures().forEach((f) => {
      if (f.get('correctionId') === correctionId) {
        extend(extent, f.getGeometry()!.getExtent());
        found = true;
      }
    });

    if (found) {
      map.getView().fit(extent, {
        padding: [100, 100, 100, 100],
        maxZoom: 19,
        duration: 500,
      });
    }
  };

  // Group by status
  const grouped = overlayCorrections.reduce(
    (acc, c) => {
      const status = c.status as CorrectionStatus;
      if (!acc[status]) acc[status] = [];
      acc[status].push(c);
      return acc;
    },
    {} as Partial<Record<CorrectionStatus, FieldCorrection[]>>,
  );

  const statusOrder: CorrectionStatus[] = ['PENDING', 'APPROVED', 'REJECTED'];
  const flatItems = statusOrder.flatMap((s) => grouped[s] ?? []);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Field Corrections
        </h3>
      </div>

      <Collapsible
        defaultOpen
        className="rounded-lg border border-border bg-card shadow-sm overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border/50">
          <CollapsibleTrigger className="group/trigger flex items-center gap-2 font-medium text-sm flex-1 text-left text-foreground hover:text-secondary transition-colors cursor-pointer select-none">
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=closed]/trigger:-rotate-90 group-hover/trigger:text-secondary" />
            <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
            Corrections ({overlayCorrections.length})
          </CollapsibleTrigger>

          <Switch
            checked={overlayVisible}
            onCheckedChange={setOverlayVisible}
            onClick={(e) => e.stopPropagation()}
            className="scale-90 bg-secondary data-[state=checked]:bg-secondary hover:cursor-pointer"
          />
        </div>

        <CollapsibleContent>
          {/* Legend */}
          <div className="flex items-center gap-4 px-4 py-2 border-b border-border/40">
            {statusOrder.map((status) => {
              const count = grouped[status]?.length ?? 0;
              if (count === 0) return null;
              const cfg = STATUS_CONFIG[status];
              return (
                <div key={status} className="flex items-center gap-1.5">
                  <div
                    className="w-2.5 h-2.5 rounded-full border"
                    style={{
                      backgroundColor: cfg.color,
                      borderColor: cfg.strokeColor,
                    }}
                  />
                  <span className="text-xs text-muted-foreground">
                    {cfg.label} ({count})
                  </span>
                </div>
              );
            })}
          </div>

          {/* Items */}
          <div>
            {flatItems.map((c, idx) => {
              const cfg =
                STATUS_CONFIG[c.status as CorrectionStatus] ??
                STATUS_CONFIG.PENDING;
              const isHighlighted = highlightedCorrectionId === c.id;
              const isPending = c.status === 'PENDING';
              const isLast = idx === flatItems.length - 1;

              return (
                <div
                  key={c.id}
                  className={cn(
                    'flex items-center justify-between py-2 px-4 transition-colors group',
                    !isLast && 'border-b border-border/40',
                    isHighlighted ? 'bg-muted/60' : 'hover:bg-muted/30',
                  )}
                  onMouseEnter={() => setHighlightedCorrection(c.id)}
                  onMouseLeave={() => setHighlightedCorrection(null)}
                >
                  {/* Left — icon + label */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="h-4 w-4 shrink-0 flex items-center justify-center">
                      <div
                        className="w-3 h-3 rounded-full border-2"
                        style={{
                          backgroundColor: cfg.color,
                          borderColor: cfg.strokeColor,
                          opacity: isHighlighted ? 1 : 0.8,
                        }}
                      />
                    </div>

                    <Label className="cursor-default text-sm truncate select-none text-foreground font-normal">
                      {c.correctionType === 'SKETCH' ? 'Sketch' : 'Relocate'}
                      {/* {c.id && (
                        <span className="text-muted-foreground font-normal ">
                          {' · '}
                          {c.id}
                        </span>
                      )} */}
                    </Label>
                  </div>

                  {/* Right — date + actions */}
                  <div className="flex items-center gap-1 ml-2 shrink-0">
                    <span className="text-xs text-muted-foreground mr-1">
                      {new Date(c.createdAt).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>

                    {/* Edit button — PENDING only */}
                    {/* {isPending && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          requestEditCorrection(c.id);
                        }}
                        className={cn(
                          'h-7 w-7 flex items-center justify-center rounded-md transition-all',
                          'text-muted-foreground hover:bg-secondary/10 hover:text-secondary',
                          isHighlighted
                            ? 'opacity-100'
                            : 'opacity-0 group-hover:opacity-100',
                        )}
                        title="Edit correction"
                      >
                        <PencilLine className="h-3.5 w-3.5" />
                      </button>
                    )} */}

                    {/* Zoom button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setHighlightedCorrection(c.id);
                        zoomToCorrection(c.id);
                      }}
                      className={cn(
                        'h-7 w-7 flex items-center justify-center rounded-md transition-all',
                        'text-muted-foreground hover:bg-secondary/10 hover:text-secondary',
                        isHighlighted
                          ? 'opacity-100'
                          : 'opacity-0 group-hover:opacity-100',
                      )}
                      title="Zoom to correction"
                    >
                      <MapPin className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
